/**
 * Product Carousel Component
 * A flexible carousel for showcasing products, powered by Swiper.js.
 */

// Dynamically load Swiper.js library
function loadSwiperJS() {
  return new Promise((resolve, reject) => {
    if (window.Swiper) {
      resolve(window.Swiper);
      return;
    }
    
    // Load CSS first
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
    document.head.appendChild(css);
    
    // Then load JS
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
    script.onload = () => resolve(window.Swiper);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

class ProductCarousel {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.init();
  }

  async init() {
    console.log('ProductCarousel init started');
    try {
      const Swiper = await loadSwiperJS();
      console.log('Swiper.js loaded successfully');
      this.initSwiper(Swiper);
    } catch (error) {
      console.error("Product Carousel: Swiper.js failed to load.", error);
      // Fallback: show products in a simple grid
      this.element.classList.add('fallback-mode');
    }
  }

  initSwiper(Swiper) {
    console.log('initSwiper called');
    const slides = this.element.querySelectorAll('.product-carousel-slide');
    console.log('Found slides:', slides.length);
    console.log('Container element:', this.element);
    console.log('Swiper wrapper:', this.element.querySelector('.swiper-wrapper'));
    
    if (slides.length === 0) {
      console.error('No slides found for Swiper initialization');
      this.element.classList.add('fallback-mode');
      return;
    }

    const slidesPerView = parseInt(this.element.dataset.slidesPerView, 10) || 5;
    const autoplay = this.element.dataset.autoplay === 'true' ? 4000 : false;
    const loop = this.element.dataset.loop === 'true';
    
    const isMobile = window.innerWidth <= 768;
    
    console.log('Swiper options:', { 
      slidesPerView, 
      autoplay, 
      loop: !isMobile && loop,
      isMobile
    });

    const swiperOptions = {
      direction: 'horizontal',
      loop: !isMobile && loop,
      
      // 使用 'auto' slidesPerView 以获得更稳定的移动端体验
      slidesPerView: isMobile ? 'auto' : slidesPerView,
      spaceBetween: isMobile ? 15 : 0,
      
      autoplay: autoplay && !isMobile ? {
        delay: autoplay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      } : false,
      
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      normalizeSlideIndex: true,
      
      // 移动端优化
      freeMode: isMobile ? {
        enabled: true,
        sticky: false,
        momentum: true,
        momentumBounce: false, // 禁用回弹，防止卡在末尾
        momentumRatio: 0.8,
        momentumVelocityRatio: 0.8,
        minimumVelocity: 0.02
      } : false,
      
      resistanceRatio: 0, // 禁用阻力
      watchSlidesProgress: true,
      
      breakpoints: {
        400: {
          slidesPerView: 2.5,
          spaceBetween: 10,
          freeMode: false,
        },
        600: {
          slidesPerView: 3,
          spaceBetween: 10,
          freeMode: false,
        },
        800: {
          slidesPerView: 4,
          spaceBetween: 10,
          freeMode: false,
        },
        1000: {
          slidesPerView: 5,
          spaceBetween: 10,
          freeMode: false,
        },
        1200: {
          slidesPerView: slidesPerView,
          spaceBetween: 10,
          freeMode: false,
        }
      },
      
      navigation: {
        nextEl: '.product-carousel-nav-button.next',
        prevEl: '.product-carousel-nav-button.prev',
      },
      
      on: {
        init: (swiper) => {
          console.log('Swiper initialized successfully');
          this.element.classList.add('loaded');
          this.updateProgressBar();
          
          setTimeout(() => {
            if (this.swiper) this.swiper.update();
          }, 100);
        },
        slideChange: () => {
          this.updateProgressBar();
        },
        progress: () => {
          this.updateProgressBar();
        },
        setTranslate: (swiper, translate) => {
          if (window.innerWidth <= 768) {
            this.updateProgressBarFromTranslate(swiper, translate);
          }
        },
      }
    };

    try {
      this.swiper = new Swiper(this.element, swiperOptions);
      console.log('Swiper instance created successfully');
    } catch (error) {
      console.error('Failed to create Swiper instance:', error);
      this.element.classList.add('fallback-mode');
      this.element.classList.remove('loaded');
    }
  }

  updateProgressBarFromTranslate(swiper, translate) {
    const parentContainer = this.element.closest('.product-carousel-wrapper');
    const thumb = parentContainer ? parentContainer.querySelector('.product-carousel-scrollbar-thumb') : null;
    
    if (!thumb || !swiper || window.innerWidth > 768) return;
    
    const wrapper = swiper.wrapperEl;
    const totalSlidesWidth = wrapper ? wrapper.scrollWidth : 0;
    const visibleWidth = swiper.width;
    const maxTranslate = Math.max(0, totalSlidesWidth - visibleWidth);
    
    if (maxTranslate <= 0) {
      thumb.style.width = '100%';
      thumb.style.left = '0%';
      return;
    }
    
    const currentTranslate = Math.abs(translate);
    const progress = Math.min(Math.max(currentTranslate / maxTranslate, 0), 1);
    
    const visibleRatio = visibleWidth / totalSlidesWidth;
    const thumbWidth = Math.max(visibleRatio * 100, 15);
    const thumbPosition = progress * (100 - thumbWidth);
    
    thumb.style.width = `${thumbWidth}%`;
    thumb.style.left = `${Math.max(0, Math.min(thumbPosition, 100 - thumbWidth))}%`;
  }

  updateProgressBar() {
    const parentContainer = this.element.closest('.product-carousel-wrapper');
    const thumb = parentContainer ? parentContainer.querySelector('.product-carousel-scrollbar-thumb') : null;
    if (!thumb || !this.swiper) return;

    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      const currentTranslate = this.swiper.translate || 0;
      this.updateProgressBarFromTranslate(this.swiper, currentTranslate);
    } else {
      // Desktop logic
      const currentIndex = this.swiper.activeIndex || 0;
      const totalSlides = this.swiper.slides.length;
      const slidesPerView = this.swiper.params.slidesPerView;
      const maxIndex = Math.max(0, totalSlides - slidesPerView);
      
      if (maxIndex === 0 || totalSlides <= slidesPerView) {
        thumb.style.width = '100%';
        thumb.style.left = '0%';
      } else {
        const progress = currentIndex / maxIndex;
        const thumbWidth = Math.max((slidesPerView / totalSlides) * 100, 15);
        const thumbPosition = progress * (100 - thumbWidth);
        thumb.style.width = `${thumbWidth}%`;
        thumb.style.left = `${Math.min(thumbPosition, 100 - thumbWidth)}%`;
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('[data-component="product-carousel"]');
  carousels.forEach(carouselElement => {
    new ProductCarousel(carouselElement);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const carouselElement = section.querySelector('[data-component="product-carousel"]');
  if (carouselElement) {
    new ProductCarousel(carouselElement);
  }
}); 